import type { Ref } from 'vue'

import { computed, onMounted, onUnmounted, ref } from 'vue'

const EXPAND_HYSTERESIS_PX = 24

/**
 * A Vue composable that collapses a set of subheader controls to their
 * compact variant when the subheader runs out of horizontal space and the
 * subheader groups start to collide.
 *
 * Relies on the layout contract of `#subheader`
 * (two_drawer_component_new.html.slim) and `#secondary-subheader`
 * (two_drawer_component.html.slim): the controls are rendered inside the
 * right group and the left group is its immediately preceding sibling. When
 * mounted outside that structure, only `forcedCompact` drives the result.
 *
 * The first render is always expanded so the expanded width is measured
 * before any compact variant is shown (onMounted runs before paint).
 *
 * @param rootRef - A ref to the controls' root element.
 * @param forcedCompact - An optional ref forcing the compact variant
 * regardless of the available space (e.g. when the documents panel is open).
 * @returns Whether the compact variant should be rendered.
 */
export default function useAutoCompact(
  rootRef: Ref<HTMLElement | null>,
  forcedCompact: Ref<boolean> = ref(false)
): Ref<boolean> {
  const autoCompact = ref(false)
  const expandedWidthMeasured = ref(false)
  const compact = computed(
    () =>
      expandedWidthMeasured.value && (forcedCompact.value || autoCompact.value)
  )

  let observer: ResizeObserver | null = null
  let expandedWidth = 0

  onMounted(() => {
    const rootEl = rootRef.value
    const subheader = rootEl?.closest<HTMLElement>(
      '#subheader, #secondary-subheader'
    )
    if (!rootEl || !subheader) {
      expandedWidthMeasured.value = true
      return
    }

    const rightGroup = Array.from(subheader.children).find((child) =>
      child.contains(rootEl)
    )
    const leftGroup = rightGroup?.previousElementSibling
    if (
      !(rightGroup instanceof HTMLElement) ||
      !(leftGroup instanceof HTMLElement)
    ) {
      expandedWidthMeasured.value = true
      return
    }

    const measure = (): void => {
      const subheaderStyle = getComputedStyle(subheader)
      const available =
        subheader.clientWidth -
        (parseFloat(subheaderStyle.paddingLeft) || 0) -
        (parseFloat(subheaderStyle.paddingRight) || 0)
      const groupsGap = parseFloat(getComputedStyle(leftGroup).marginRight) || 0
      const required =
        leftGroup.scrollWidth + groupsGap + rightGroup.scrollWidth

      if (compact.value) {
        const requiredWhenExpanded =
          required -
          rootEl.offsetWidth +
          Math.max(expandedWidth, rootEl.offsetWidth)
        autoCompact.value =
          requiredWhenExpanded + EXPAND_HYSTERESIS_PX > available
      } else {
        expandedWidth = rootEl.offsetWidth
        autoCompact.value = required > available
      }
    }

    measure()
    expandedWidthMeasured.value = true

    observer = new ResizeObserver(measure)
    observer.observe(subheader)
    observer.observe(leftGroup)
    observer.observe(rightGroup)
    observer.observe(rootEl)
  })

  onUnmounted(() => {
    observer?.disconnect()
  })

  return compact
}
